import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Power, KeyRound, Eye, EyeOff, Shield } from "lucide-react";

// Owner can create/manage these roles (never another OWNER)
const ROLE_OPTIONS = [
  { value: "ADMIN", label: "Admin (multi-branch)" },
  { value: "BRANCH_MANAGER", label: "Branch Manager" },
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "RECEPTION", label: "Receptionist" },
  { value: "ACCOUNTANT", label: "Accountant" },
  { value: "STAFF", label: "Staff" },
];
const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));
const EMPTY = { name: "", email: "", password: "", role: "BRANCH_MANAGER", branch_id: "", branch_ids: [], phone: "" };
const isMultiBranch = (role) => role === "ADMIN";
const branchScoped = (role) => role === "BRANCH_MANAGER" || role === "RECEPTION" || role === "STAFF" || role === "ACCOUNTANT";

export default function ManagerAccounts() {
  const { user } = useAuth();
  const { branches } = useBranch();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [perm, setPerm] = useState(null); // {u, groups, values}
  const [pwUser, setPwUser] = useState(null);
  const [pwVal, setPwVal] = useState("");

  const PRESET_MANAGER = ["dashboard_view","appointment_view","add_appointment","edit_appointment","cancel_appointment","billing_create","billing_view","billing_report","edit_bill","customer_view","add_customer","edit_customer","export_customer_pdf","export_customer_excel","staff_view","attendance_view","attendance_edit","salary_view","incentive_view","services_view","service_menu","products_view","inventory_view","add_inventory","edit_inventory","packages_view","expenses_view","add_expense","reports_view","reports_export","whatsapp_access"];
  const PRESET_RECEPTION = ["dashboard_view","appointment_view","add_appointment","edit_appointment","cancel_appointment","billing_create","billing_view","customer_view","add_customer","edit_customer","services_view","service_menu"];

  const savePw = async () => {
    if (!pwVal || pwVal.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    try { await api.put(`/users/${pwUser.id}`, { password: pwVal }); toast.success("Password changed"); setPwUser(null); setPwVal(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openPerms = async (u) => {
    try {
      const [cat, cur] = await Promise.all([
        api.get("/users/permissions/catalog"),
        api.get(`/users/${u.id}/permissions`),
      ]);
      setPerm({ u, groups: cat.data.groups, values: cur.data.permissions || {} });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setPermVal = (k, v) => setPerm((p) => ({ ...p, values: { ...p.values, [k]: v } }));
  const allKeys = () => perm.groups.flatMap((g) => g.items.map(([k]) => k));
  const selectAll = () => setPerm((p) => ({ ...p, values: Object.fromEntries(allKeys().map((k) => [k, true])) }));
  const clearAll = () => setPerm((p) => ({ ...p, values: Object.fromEntries(allKeys().map((k) => [k, false])) }));
  const applyPreset = (keys) => setPerm((p) => ({ ...p, values: Object.fromEntries(allKeys().map((k) => [k, keys.includes(k)])) }));
  const savePerms = async () => {
    try {
      await api.put(`/users/${perm.u.id}/permissions`, { permissions: perm.values });
      toast.success("Permissions saved — applies immediately");
      setPerm(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const isOwner = user?.role === "OWNER";
  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  if (!isOwner) {
    return <Card className="p-10 bg-card border-border text-center text-muted-foreground" data-testid="managers-denied">Access denied — Owner only.</Card>;
  }

  const openNew = () => { setEditId(null); setForm(EMPTY); setShowPw(false); setOpen(true); };
  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ name: u.name || "", email: u.email || "", password: "", role: u.role, branch_id: u.branch_id || "", branch_ids: u.branch_ids || [], phone: u.phone || "" });
    setShowPw(false); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!editId && !form.email.trim()) return toast.error("Email is required");
    if (!editId && form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (branchScoped(form.role) && !form.branch_id) return toast.error("Select an assigned branch for this role");
    if (isMultiBranch(form.role) && !(form.branch_ids || []).length) return toast.error("Assign at least one branch to this Admin");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), role: form.role, phone: form.phone || null,
        branch_id: branchScoped(form.role) ? form.branch_id : null,
        branch_ids: isMultiBranch(form.role) ? form.branch_ids : [],
      };
      if (editId) {
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("Account updated");
      } else {
        await api.post("/users", { ...payload, email: form.email.trim().toLowerCase(), password: form.password });
        toast.success("Account created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    try {
      if (u.status === "inactive") await api.put(`/users/${u.id}`, { status: "active" });
      else await api.delete(`/users/${u.id}`);
      toast.success(u.status === "inactive" ? "Account activated" : "Account deactivated");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const managed = users.filter((u) => u.role !== "OWNER");

  return (
    <div>
      <PageHeader
        title="Manager Accounts"
        subtitle="Create and manage Branch Managers & Super Admins — no database access needed"
        actions={<Button onClick={openNew} data-testid="add-manager-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Account</Button>}
      />
      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Name</TableHead><TableHead>Email</TableHead>
              <TableHead>Role</TableHead><TableHead>Branch</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {managed.map((u) => (
              <TableRow key={u.id} className="border-border" data-testid={`manager-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={u.role === "SUPER_ADMIN" ? "border-primary text-primary" : "border-sky-400/40 text-sky-300"}>{ROLE_LABEL[u.role] || u.role}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{u.role === "SUPER_ADMIN" ? "Any (one at a time)" : u.role === "ADMIN" ? ((u.branch_ids || []).map(branchName).join(", ") || "—") : branchName(u.branch_id)}</TableCell>
                <TableCell>
                  {u.status === "inactive"
                    ? <Badge className="bg-red-500/20 text-red-300">Inactive</Badge>
                    : <Badge className="bg-green-500/20 text-green-300">Active</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit / Change Password" onClick={() => openEdit(u)} data-testid={`edit-manager-${u.id}`}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-300" title="Change Password" onClick={() => { setPwUser(u); setPwVal(""); }} data-testid={`pw-manager-${u.id}`}><KeyRound size={14} /></Button>
                    {["ADMIN", "BRANCH_MANAGER", "RECEPTION"].includes(u.role) && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" title="Permissions" onClick={() => openPerms(u)} data-testid={`perms-manager-${u.id}`}><Shield size={14} /></Button>
                    )}
                    <Button size="icon" variant="ghost" className={`h-8 w-8 ${u.status === "inactive" ? "text-green-400" : "text-orange-300"}`} title={u.status === "inactive" ? "Activate" : "Deactivate"} onClick={() => toggleStatus(u)} data-testid={`toggle-manager-${u.id}`}><Power size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!managed.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No manager accounts yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit" : "Add"} Account</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary mt-1" data-testid="manager-name" /></div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-secondary mt-1" data-testid="manager-email" />
              {editId && <p className="text-[11px] text-muted-foreground mt-1">Email cannot be changed after creation.</p>}
            </div>
            <div>
              <Label className="text-xs">{editId ? "Reset Password (optional)" : "Password *"}</Label>
              <div className="relative mt-1">
                <Input type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-secondary pr-10" data-testid="manager-password" placeholder={editId ? "Leave blank to keep current" : ""} />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="manager-toggle-pw">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, branch_id: branchScoped(v) ? form.branch_id : "" })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="manager-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {branchScoped(form.role) ? (
              <div>
                <Label className="text-xs">Assigned Branch *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="manager-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : isMultiBranch(form.role) ? (
              <div>
                <Label className="text-xs">Assigned Branches * <span className="text-muted-foreground">(pick one or many)</span></Label>
                <div className="mt-1 grid grid-cols-2 gap-2 rounded-sm border border-border bg-secondary p-2" data-testid="admin-branches">
                  {branches.map((b) => {
                    const on = (form.branch_ids || []).includes(b.id);
                    return (
                      <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`admin-branch-${b.id}`}>
                        <Checkbox checked={on} onCheckedChange={(v) => setForm({ ...form, branch_ids: v ? [...(form.branch_ids || []), b.id] : (form.branch_ids || []).filter((x) => x !== b.id) })} />
                        {b.name}
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Admin sees only these branches, with an "All Assigned Branches" combined view. Permissions start OFF — enable them per Admin below.</p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground flex items-center gap-1"><KeyRound size={12} /> Super Admin is not tied to a branch — they select one branch at a time.</p>
            )}
            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-secondary mt-1" data-testid="manager-phone" /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="manager-save">{saving ? "Saving…" : editId ? "Save Changes" : "Create Account"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions panel */}
      <Dialog open={!!perm} onOpenChange={(o) => !o && setPerm(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="perms-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{perm?.u.role === "RECEPTION" ? "Receptionist" : "Manager"} Access Permissions</DialogTitle>
          </DialogHeader>
          {perm && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant="outline" className="border-primary text-primary" data-testid="perms-role">Role: {perm.u.role === "RECEPTION" ? "Receptionist" : "Manager"}</Badge>
                <Badge variant="outline" data-testid="perms-branch">Branch: {branchName(perm.u.branch_id)}</Badge>
                <span className="ml-auto flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => applyPreset(perm.u.role === "RECEPTION" ? PRESET_RECEPTION : PRESET_MANAGER)} data-testid="perms-preset">Typical {perm.u.role === "RECEPTION" ? "Receptionist" : "Manager"}</Button>
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={selectAll} data-testid="perms-select-all">Select All</Button>
                  <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={clearAll} data-testid="perms-clear-all">Clear All</Button>
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Owner-only functions (add/delete/archive branch, super-admin & owner settings, system security) are never available to Managers or Receptionists.</p>
              {perm.groups.map((g) => (
                <div key={g.group}>
                  <h4 className="text-sm font-semibold text-primary mb-1">{g.group}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {g.items.map(([k, label]) => (
                      <label key={k} className="flex items-center justify-between gap-2 py-1 text-sm cursor-pointer" data-testid={`perm-${k}`}>
                        <span>{label}</span>
                        <input type="checkbox" checked={!!perm.values[k]} onChange={(e) => setPermVal(k, e.target.checked)} className="h-4 w-4 accent-[#C9A24B]" data-testid={`perm-toggle-${k}`} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button onClick={savePerms} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="perms-save">Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Change Password */}
      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="pw-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Change Password</DialogTitle></DialogHeader>
          {pwUser && <p className="text-sm text-muted-foreground -mt-2">{pwUser.name} · {pwUser.email}</p>}
          <Input type="text" value={pwVal} onChange={(e) => setPwVal(e.target.value)} placeholder="New password" className="bg-secondary" data-testid="pw-input" />
          <DialogFooter><Button onClick={savePw} className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]" data-testid="pw-save">Update Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
